import {CanvasTexture, Euler, LinearFilter, Matrix4, Quaternion, SRGBColorSpace, Vector3} from "three";
import {MM_TO_M} from "../module3d/panelMeshTransform";
import {moduleDimensionsMmToSizeM, normalizeModuleSizeM} from "../module3d/snapPlacement";
import {getModuleCompositionDimension} from "../../../../utils/mapModuleCompositionToModuleItem";

/** ~0.15 mm от грани (минимум против z-fight). */
export const MODULE_PRIORITY_BADGE_FACE_OFFSET_M = 0.00015;

/** Габариты «наклейки» на грани (м). */
export const MODULE_PRIORITY_BADGE_WIDTH_M = 0.022;
export const MODULE_PRIORITY_BADGE_HEIGHT_M = 0.018;

const drawRoundRect = (ctx, x, y, width, height, radius) => {
    const r = Math.min(radius, width / 2, height / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
};

/** Текстура плашки приоритета — та же геометрия в перспективе и ортографии. */
export const createModulePriorityBadgeTexture = (label) => {
    const text = String(label);
    const multiChar = text.length > 1;
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return null;
    }

    const cx = size / 2;
    const cy = size / 2;
    const pillW = multiChar ? 88 : 72;
    const pillH = 72;

    ctx.fillStyle = "#006CCC";
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 4;
    if (multiChar) {
        drawRoundRect(ctx, cx - pillW / 2, cy - pillH / 2, pillW, pillH, pillH / 2);
        ctx.stroke();
    } else {
        ctx.beginPath();
        ctx.arc(cx, cy, pillH / 2 - 2, 0, Math.PI * 2);
        ctx.stroke();
    }

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 36px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cx, cy + 1);

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.generateMipmaps = false;
    texture.minFilter = texture.magFilter = LinearFilter;
    texture.needsUpdate = true;
    return texture;
};

const badgeTextureCache = new Map();

/** Кэш canvas-текстур (не dispose на каждый mount — иначе Strict Mode гасит плашки). */
export const getModulePriorityBadgeTexture = (label) => {
    const key = String(label);
    if (!badgeTextureCache.has(key)) {
        const texture = createModulePriorityBadgeTexture(key);
        if (texture) {
            badgeTextureCache.set(key, texture);
        }
    }
    return badgeTextureCache.get(key) ?? null;
};

/**
 * Шесть граней зелёного куба: угол (0,0,0), +X/+Y/+Z внутрь.
 * origin — нижний левый угол грани; tangentU/V — U по X, V по Y на лицевой; normal наружу.
 */
const buildModuleShellFaces = (width, height, depth) => {
    const widthMm = width / MM_TO_M;
    const heightMm = height / MM_TO_M;
    const depthMm = depth / MM_TO_M;

    return [
        {
            origin: new Vector3(0, 0, 0),
            tangentU: new Vector3(1, 0, 0),
            tangentV: new Vector3(0, 1, 0),
            normal: new Vector3(0, 0, -1),
            extentUMm: widthMm,
            extentVMm: heightMm,
        },
        {
            origin: new Vector3(0, 0, depth),
            tangentU: new Vector3(1, 0, 0),
            tangentV: new Vector3(0, 1, 0),
            normal: new Vector3(0, 0, 1),
            extentUMm: widthMm,
            extentVMm: heightMm,
        },
        {
            origin: new Vector3(0, 0, 0),
            tangentU: new Vector3(0, 0, 1),
            tangentV: new Vector3(0, 1, 0),
            normal: new Vector3(-1, 0, 0),
            extentUMm: depthMm,
            extentVMm: heightMm,
        },
        {
            origin: new Vector3(width, 0, 0),
            tangentU: new Vector3(0, 0, -1),
            tangentV: new Vector3(0, 1, 0),
            normal: new Vector3(1, 0, 0),
            extentUMm: depthMm,
            extentVMm: heightMm,
        },
        {
            origin: new Vector3(0, 0, 0),
            tangentU: new Vector3(1, 0, 0),
            tangentV: new Vector3(0, 0, 1),
            normal: new Vector3(0, -1, 0),
            extentUMm: widthMm,
            extentVMm: depthMm,
        },
        {
            origin: new Vector3(0, height, 0),
            tangentU: new Vector3(1, 0, 0),
            tangentV: new Vector3(0, 0, -1),
            normal: new Vector3(0, 1, 0),
            extentUMm: widthMm,
            extentVMm: depthMm,
        },
    ];
};

/**
 * Лицевая грань в СK модуля: плоскость z = depth (+Z от угла 0,0,0).
 * coordinateX3DPoint / coordinateY3DPoint — мм от BL этой грани (U по +X, V по +Y).
 */
export const resolveModuleFrontFaceFrameLocal = ({moduleSizeM} = {}) => {
    const {width, height, depth} = normalizeModuleSizeM(moduleSizeM);
    const faces = buildModuleShellFaces(width, height, depth);
    return faces[1];
};

/** С бэка часто приходит 0/0 — трактуем как «ещё не задано», центр передней грани. */
export const areModulePriorityCoordinates3dUnset = (coordinateX3DPoint, coordinateY3DPoint) => {
    const xEmpty = coordinateX3DPoint == null || coordinateX3DPoint === "";
    const yEmpty = coordinateY3DPoint == null || coordinateY3DPoint === "";
    if (xEmpty && yEmpty) {
        return true;
    }
    const nx = Number(coordinateX3DPoint);
    const ny = Number(coordinateY3DPoint);
    return nx === 0 && ny === 0;
};

const buildFaceAlignedRotation = (frame) => {
    const tangentU = frame.tangentU.clone().normalize();
    const tangentV = frame.tangentV.clone().normalize();
    const normal = frame.normal.clone().normalize();
    const expectedNormal = new Vector3().crossVectors(tangentU, tangentV).normalize();
    if (expectedNormal.lengthSq() > 1e-12 && expectedNormal.dot(normal) < 0) {
        tangentV.negate();
    }
    const basis = new Matrix4().makeBasis(tangentU, tangentV, normal);
    const quaternion = new Quaternion().setFromRotationMatrix(basis);
    if (!Number.isFinite(quaternion.x)) {
        return [0, 0, 0];
    }
    const euler = new Euler().setFromQuaternion(quaternion, "XYZ");
    if (
        !Number.isFinite(euler.x)
        || !Number.isFinite(euler.y)
        || !Number.isFinite(euler.z)
    ) {
        return [0, 0, 0];
    }
    return [euler.x, euler.y, euler.z];
};

/**
 * coordinateX3DPoint / coordinateY3DPoint — мм от BL выбранной передней грани, центр плашки.
 * null / 0,0 → центр грани.
 */
export const resolveModulePriorityMarkerLocalPose = ({
    moduleSizeM,
    coordinateX3DPoint,
    coordinateY3DPoint,
} = {}) => {
    const {width, height, depth} = normalizeModuleSizeM(moduleSizeM);

    const frame = resolveModuleFrontFaceFrameLocal({moduleSizeM});

    const extentU = Math.max(Number(frame.extentUMm) || 0, 1);
    const extentV = Math.max(Number(frame.extentVMm) || 0, 1);

    const unset = areModulePriorityCoordinates3dUnset(
        coordinateX3DPoint,
        coordinateY3DPoint,
    );

    const parseCoordMm = (value, fallbackMm) => {
        if (value == null || value === "") {
            return fallbackMm;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : fallbackMm;
    };

    const defaultUMm = extentU / 2;
    const defaultVMm = extentV / 2;
    const uMm = Math.min(
        extentU,
        Math.max(0, unset ? defaultUMm : parseCoordMm(coordinateX3DPoint, defaultUMm)),
    );
    const vMm = Math.min(
        extentV,
        Math.max(0, unset ? defaultVMm : parseCoordMm(coordinateY3DPoint, defaultVMm)),
    );

    const surfacePoint = frame.origin
        .clone()
        .add(frame.tangentU.clone().multiplyScalar(uMm * MM_TO_M))
        .add(frame.tangentV.clone().multiplyScalar(vMm * MM_TO_M));

    const normal = frame.normal.clone().normalize();
    const position = surfacePoint.add(
        normal.multiplyScalar(MODULE_PRIORITY_BADGE_FACE_OFFSET_M),
    );

    const px = position.x;
    const py = position.y;
    const pz = position.z;
    if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) {
        return {
            position: [width / 2, height / 2, depth],
            rotation: [0, 0, 0],
        };
    }

    return {
        position: [px, py, pz],
        rotation: buildFaceAlignedRotation(frame),
    };
};

export const getModulePriorityLabel = (row) => {
    if (row == null) {
        return null;
    }
    const priority = row.priority ?? row.module?.priority;
    if (priority == null || priority === "") {
        return null;
    }
    return String(priority);
};

/** id строки module-composition для PATCH (не id композиции и не module.id). */
export const getModuleCompositionRowPatchId = (row) => {
    const candidate = row?.moduleCompositionId ?? row?.id;
    if (candidate == null || candidate === "" || Number(candidate) === 0) {
        return null;
    }
    return candidate;
};

const resolveModulePriorityCoordinates3dDefaultsMm = (row) => {
    const moduleSizeM = getModulePriorityMarkerSizeM(row);
    const frame = resolveModuleFrontFaceFrameLocal({moduleSizeM});
    const defaultX = Math.round(Math.max(Number(frame.extentUMm) || 0, 1) / 2);
    const defaultY = Math.round(Math.max(Number(frame.extentVMm) || 0, 1) / 2);
    return {defaultX, defaultY};
};

/** min 0, max 2× дефолт (центр грани) — для инпутов координат приоритета на сцене 3D. */
export const getModulePriorityCoordinates3dAxisLimitsMm = (row) => {
    const {defaultX, defaultY} = resolveModulePriorityCoordinates3dDefaultsMm(row);
    return {
        x: {min: 0, max: defaultX * 2, default: defaultX},
        y: {min: 0, max: defaultY * 2, default: defaultY},
    };
};

/** Значения для инпутов: с бэка или центр передней грани (мм), как у плашки на сцене. */
export const resolveModulePriorityCoordinates3dDisplayMm = (row) => {
    const {defaultX, defaultY} = resolveModulePriorityCoordinates3dDefaultsMm(row);
    if (areModulePriorityCoordinates3dUnset(row?.coordinateX3DPoint, row?.coordinateY3DPoint)) {
        return {
            coordinateX3DPoint: defaultX,
            coordinateY3DPoint: defaultY,
        };
    }
    return {
        coordinateX3DPoint: Math.round(Number(row.coordinateX3DPoint)),
        coordinateY3DPoint: Math.round(Number(row.coordinateY3DPoint)),
    };
};

/** Query-параметры PATCH …/module-compositions/{id}/update-coordinate-3d */
export const buildModuleCompositionCoordinate3dPatchParams = (
    coordinateX3DPoint,
    coordinateY3DPoint,
) => {
    const x = Number(coordinateX3DPoint);
    const y = Number(coordinateY3DPoint);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }
    return {
        coordinateX3DPoint: Math.round(x),
        coordinateY3DPoint: Math.round(y),
    };
};

/** @deprecated use buildModuleCompositionCoordinate3dPatchParams for query PATCH */
export const buildModuleCompositionCoordinate3dPatchBody = (
    coordinateX3DPoint,
    coordinateY3DPoint,
) => ({
    coordinateX3DPoint:
        coordinateX3DPoint == null || coordinateX3DPoint === ""
            ? null
            : Number(coordinateX3DPoint),
    coordinateY3DPoint:
        coordinateY3DPoint == null || coordinateY3DPoint === ""
            ? null
            : Number(coordinateY3DPoint),
});

/** moduleSizeM из строки module-composition (те же габариты, что зелёный куб). */
export const getModulePriorityMarkerSizeM = (row) => {
    const width = Math.max(Number(getModuleCompositionDimension(row, "width")) || 600, 1);
    const height = Math.max(Number(getModuleCompositionDimension(row, "height")) || 720, 1);
    const depth = Math.max(Number(getModuleCompositionDimension(row, "depth")) || 560, 1);
    return moduleDimensionsMmToSizeM(width, height, depth);
};
